import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { heroDataNew } from '../mock/programsData';
import { tiktokTrack } from '../lib/tiktokPixel';

// Standard Scrabble letter values
const TILE_VALUES = {
  A:1,B:3,C:3,D:2,E:1,F:4,G:2,H:4,I:1,J:8,K:5,L:1,M:3,
  N:1,O:1,P:3,Q:10,R:1,S:1,T:1,U:1,V:4,W:4,X:8,Y:4,Z:10
};

const ScrabbleTile = ({ letter, rotate = 0 }) => (
  <div
    className="relative inline-flex items-center justify-center font-extrabold text-amber-900 shadow-[0_2px_4px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.6)]"
    style={{
      width: 'clamp(28px, 4.5vw, 44px)',
      height: 'clamp(28px, 4.5vw, 44px)',
      background: 'linear-gradient(145deg, #f5e3c0 0%, #e6cf9c 100%)',
      border: '1px solid #b89868',
      borderRadius: '6px',
      transform: `rotate(${rotate}deg)`,
      fontFamily: '"Georgia", "Times New Roman", serif',
      fontSize: 'clamp(14px, 2.2vw, 22px)',
    }}
  >
    {letter}
    <span
      className="absolute font-bold text-amber-900"
      style={{
        bottom: '2px',
        right: '4px',
        fontSize: 'clamp(7px, 1vw, 10px)',
        lineHeight: 1,
      }}
    >
      {TILE_VALUES[letter] || 1}
    </span>
  </div>
);

const ScrabbleWord = ({ word, tilt = '0deg' }) => (
  <div
    className="flex gap-1 sm:gap-1.5"
    style={{ transform: `rotate(${tilt})` }}
  >
    {word.split('').map((ch, i) => (
      <ScrabbleTile
        key={`${word}-${i}`}
        letter={ch}
        rotate={(i % 2 === 0 ? 1 : -1) * (Math.abs(i - 2) * 0.6)}
      />
    ))}
  </div>
);

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden"
    >
      {/* Decorative blobs */}
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-pink-200 rounded-full opacity-40 blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-blue-200 rounded-full opacity-40 blur-3xl pointer-events-none"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div className="text-left space-y-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight pb-3">
              <span
                className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent inline-block"
                style={{ lineHeight: '1.3', paddingBottom: '0.15em' }}
              >
                Hola Spanglish
              </span>
            </h1>

            <h2 className="text-2xl md:text-3xl font-bold text-purple-700 leading-snug">
              Personalized Spanish Coaching with an Argentine Tutor
            </h2>

            <p className="text-base md:text-lg text-gray-700 max-w-xl leading-relaxed">
              {heroDataNew.subheadline} Learn with a native Argentine tutor who adapts every session to you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-cta-programs"
                size="lg"
                onClick={() => {
                  tiktokTrack('ClickButton', { content_name: 'Explore Programs', content_type: 'cta', location: 'hero' });
                  scrollTo('programs');
                }}
                className="bg-gradient-to-r from-pink-300 to-purple-300 hover:from-pink-400 hover:to-purple-400 text-purple-800 font-bold px-8 py-6 text-base md:text-lg rounded-full border-2 border-purple-200 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                {heroDataNew.ctaPrimary}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="hero-cta-book"
                size="lg"
                variant="outline"
                onClick={() => scrollTo('contact')}
                className="border-3 border-purple-300 bg-white text-purple-700 hover:bg-purple-50 hover:text-purple-800 font-bold px-8 py-6 text-base md:text-lg rounded-full transition-all duration-300 hover:scale-105"
              >
                {heroDataNew.ctaSecondary}
              </Button>
            </div>

            {/* Trust strip */}
            <div className="flex flex-wrap gap-8 pt-6 border-t-2 border-purple-200">
              <div>
                <div className="text-2xl">🌍</div>
                <div className="text-sm font-bold text-purple-700">Students worldwide</div>
              </div>
              <div>
                <div className="text-2xl">💬</div>
                <div className="text-sm font-bold text-purple-700">Real conversation focus</div>
              </div>
              <div>
                <div className="text-2xl">👩‍🏫</div>
                <div className="text-sm font-bold text-purple-700">Native speakers</div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-purple-200">
              <img
                src="https://customer-assets.emergentagent.com/job_fbe8e20b-7d83-4ae5-9a9f-e2c41528075e/artifacts/c9yji7xr_descarga.webp"
                alt="Learn Spanish Online with Hola Spanglish"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"></div>

              {/* Scrabble tiles overlay - bottom right of table */}
              <div className="absolute bottom-6 right-4 sm:right-8 flex flex-col gap-2 sm:gap-2.5 items-end pointer-events-none select-none" data-testid="scrabble-overlay">
                <ScrabbleWord word="LEARN"   tilt="-3deg" />
                <ScrabbleWord word="SPANISH" tilt="2deg"  />
                <ScrabbleWord word="ONLINE"  tilt="-1deg" />
              </div>
            </div>

            <div className="absolute -bottom-6 -left-6 bg-white p-5 rounded-2xl shadow-2xl border-3 border-pink-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-200 to-pink-300 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🎓</span>
                </div>
                <div>
                  <div className="font-bold text-purple-700">Personalized</div>
                  <div className="text-xs text-purple-500">1-on-1 coaching</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
